/**
 * 🔗 **주소가 비어 있는 링크·가게** — 아이가 눌러도 갈 곳이 없는 자리.
 *
 * ## 왜 shared 로 옮겼나 (2026-09-03)
 * 이 판정은 **관리자 화면 안에**(`ReleaseReady.tsx` 의 `countBrokenLinks`) 있었다.
 * 그래서 정작 **고치는 화면**(`ZoneEditor`)이 같은 걸 볼 수가 없었다 — 「출시 준비」는
 * 「주소가 빈 링크·가게 6개」라고 세는데, [🗺️ 체험공간 배치]는 **어느 것인지 아무 말도 안 했다.**
 * 벽에 갇힌 것에서 겪은 것과 **같은 모양**이다(같은 날 고쳤다).
 *
 * 화면마다 다시 짜면 「관리자에선 괜찮다는데 아이는 못 간다」가 된다 — 판정은 여기 하나다.
 *
 * ## 자리표시자도 「비었다」로 센다
 * `REPLACE` 가 든 주소는 **아직 안 채운 것**이다. 아이 화면은 그걸 걸러 「준비 중이에요」로
 * 보여 주므로(불변식 7·`configuredUrl`), 여기서도 없는 것으로 세야 숫자가 화면과 맞는다.
 */
// 🔴 확장자를 붙인다 — `--experimental-strip-types` 로 도는 스크립트(`check:livemaps`)가
//    이 파일을 직접 읽는다. vite·vitest 는 알아서 찾지만 node 는 못 찾는다.
//    옆 `mapStuck.ts` 도 같은 이유로 붙여 두었다.
import { isPlaceholderUrl } from "./placeholder.ts";

export interface BrokenLink {
  /** 운영자에게 보여 줄 이름(속성 `id`, 없으면 오브젝트 이름). */
  id: string;
  /**
   * Tiled 오브젝트의 `name` — **화면에서 그것을 찾는 열쇠**다.
   * 🔴 `id` 와 같을 거라 가정하면 안 된다(`mapStuck` 의 `StuckObject` 와 같은 이유).
   */
  name: string;
  /** `link` · `store`. */
  type: string;
}

/** 이 지도에서 주소가 비었거나 아직 자리표시자인 링크·가게. 지도 모양이 아니면 빈 배열. */
export function brokenLinks(map: unknown): BrokenLink[] {
  if (!map || typeof map !== "object") return [];
  // 🔴 배열인지 본다 — `?? []` 만으로는 숫자·객체가 그대로 지나가 `for…of` 에서 던졌다(2026-10-11 검사로 확인).
  //    이 함수는 관리자 [출시 준비] 수집에서 돌아서, 던지면 그 화면이 「살펴보는 중…」에서 멈춘다.
  const rawLayers = (map as { layers?: unknown }).layers;
  const layers: unknown[] = Array.isArray(rawLayers) ? rawLayers : [];
  const out: BrokenLink[] = [];
  for (const layer of layers) {
    const objects = (layer as { objects?: unknown } | null)?.objects;
    for (const raw of Array.isArray(objects) ? objects : []) {
      if (!raw || typeof raw !== "object") continue;
      const o = raw as {
        name?: string;
        type?: unknown;
        class?: unknown;
        properties?: { name?: string; value?: unknown }[];
      };
      // 최신 Tiled 는 class, 예전 판은 type — 우리 맵은 type 이지만 둘 다 받는다.
      // 🔴 `??` 가 아니라 `||` 다: Tiled 는 종류가 없으면 **빈 문자열**을 넣는다.
      const type = String(o.type || o.class || "");
      if (type !== "link" && type !== "store") continue;
      const props = Array.isArray(o.properties) ? o.properties : [];
      const url = String(props.find((p) => p?.name === "url")?.value ?? "").trim();
      if (url && !isPlaceholderUrl(url, false)) continue;
      const idProp = props.find((p) => p?.name === "id");
      out.push({
        id: String(idProp?.value ?? o.name ?? "(이름없음)"),
        name: String(o.name ?? ""),
        type,
      });
    }
  }
  return out;
}

/** 사람이 읽을 한 줄 — 가게와 링크는 아이가 겪는 일이 다르다. */
export function brokenLinkSay(o: BrokenLink): string {
  return o.type === "store"
    ? "아이가 상품을 열어도 「준비 중이에요」만 보여요."
    : "눌러도 갈 곳이 없어요.";
}
